import React from 'react';
import { useIntl } from 'react-intl';

import { css, cssMap } from '@atlaskit/css';
import { Box, Stack, Text } from '@atlaskit/primitives/compiled';
import { ModalBody, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { IconButton, LinkButton } from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/core/cross';
import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';
import Image from '@atlaskit/image';
import ArrowRightIcon from '@atlaskit/icon/core/arrow-right';
import AddIcon from '@atlaskit/icon/core/add';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { useTemplateDataTransform } from '../hooks/useTemplateDataTransform.tsx';
import { useUserTeamType } from '../hooks/useUserTeamType.ts';
import { useAllTemplatesQuery } from '../hooks/useAllTemplatesQuery.tsx';
import { useHybridLinkClick } from '../hooks/useHybridLinkClick.ts';

import { TemplateCard } from './TemplateCard.tsx';
import { messages } from './TemplateModal.i18n.ts';

export interface TemplateModalProps {
	onClose: () => void;
	onCloseComplete: (element?: HTMLElement) => void;
	spaceKey?: string;
	parentId?: string;
	/** Override the team type — used in fixtures/testing to preview templates for a specific team. Falls back to useUserTeamType in production. */
	teamTypeOverride?: string;
	testId?: string;
}

const stylesMap = cssMap({
	modalContainerStyles: {
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		backgroundColor: token('elevation.surface'),
	},
	headerStyles: {
		display: 'flex',
		flexDirection: 'column',
		width: '100%',
	},
	footerStyles: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		borderTop: `${token('border.width')} solid ${token('color.border')}`,
		padding: token('space.200'),
	},
	headerAndImageStyles: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: 0,
	},
	scribbleStyles: {
		alignSelf: 'flex-end',
		marginTop: token('space.negative.150'),
	},
	titleContainerStyles: {
		display: 'flex',
		flexDirection: 'column',
		gap: 0,
		alignItems: 'center',
	},
	iconButtonStyles: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
});

const headerContainerStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.0'),
	textAlign: 'center',
	width: '100%',
});

const gridStyles = css({
	display: 'grid',
	gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
	gap: token('space.300'),
	margin: token('space.400'),
});

const closeButtonStyles = css({
	display: 'flex',
	justifyContent: 'flex-end',
	width: '100%',
});

export const TemplateModal: React.FC<TemplateModalProps> = ({
	onClose,
	onCloseComplete,
	spaceKey,
	parentId,
	teamTypeOverride,
	testId = 'template-modal',
}) => {
	const { teamType: userTeamType } = useUserTeamType({ skip: !!teamTypeOverride });
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const teamType = teamTypeOverride ?? userTeamType;
	const { blueprintModuleKeyToLocalizedName } = useAllTemplatesQuery();
	const { templates, blankDocCreateLink = '' } = useTemplateDataTransform(
		teamType,
		spaceKey,
		parentId,
		blueprintModuleKeyToLocalizedName,
	);
	const { formatMessage } = useIntl();

	const handleClose = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: 'cfind-5291-template-touchpoint',
				action: 'closed',
				actionSubject: 'modal',
				actionSubjectId: 'templateModal',
			},
		}).fire();
		onClose();
	};

	const hybridBlankDocClick = useHybridLinkClick(blankDocCreateLink);
	const handleBlankDocClick = React.useCallback(
		(e: React.MouseEvent | React.KeyboardEvent) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'cfind-5291-template-touchpoint',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'startBlankDoc',
				},
			}).fire();
			onClose();
			hybridBlankDocClick(e);
		},
		[onClose, hybridBlankDocClick, createAnalyticsEvent],
	);

	const viewMoreTemplatesHref = '/wiki/templates';
	const hybridViewMoreClick = useHybridLinkClick(viewMoreTemplatesHref);
	const handleViewMoreTemplatesClick = React.useCallback(
		(e: React.MouseEvent | React.KeyboardEvent) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'cfind-5291-template-touchpoint',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'viewMoreTemplates',
				},
			}).fire();
			onClose();
			hybridViewMoreClick(e);
		},
		[onClose, hybridViewMoreClick, createAnalyticsEvent],
	);

	const topSixTemplates = templates?.slice(0, 6); // Limit to 6 templates

	const { Modal } = useChoreographerAssets();

	return (
		<Modal
			onClose={onClose}
			onCloseComplete={onCloseComplete}
			testId={testId}
			width="x-large"
			shouldScrollInViewport
		>
			<ModalHeader>
				<Stack xcss={stylesMap.headerStyles}>
					<div css={closeButtonStyles}>
						<IconButton
							appearance="subtle"
							label={formatMessage(messages.closeModalLabel)}
							onClick={handleClose}
							icon={CrossIcon}
							testId={`${testId}-close`}
						/>
					</div>
					<div css={headerContainerStyles}>
						<ModalTitle>
							<Box xcss={stylesMap.headerAndImageStyles}>
								<Box xcss={stylesMap.titleContainerStyles}>
									<Heading size="large" as="h1">
										{formatMessage(messages.modalTitle)}
									</Heading>
									<Box xcss={stylesMap.scribbleStyles}>
										<Image
											src="https://dam-cdn.atl.orangelogic.com/AssetLink/5ai2us5tokkn17261xufm2a4q1836188.svg"
											alt=""
										/>
									</Box>
								</Box>
							</Box>
						</ModalTitle>
						<Text size="medium">{formatMessage(messages.modalSubtitle)}</Text>
					</div>
				</Stack>
			</ModalHeader>

			<Box xcss={stylesMap.modalContainerStyles}>
				<ModalBody>
					<div css={gridStyles}>
						{topSixTemplates?.map((template, index) => {
							return (
								<TemplateCard
									key={`${template.name}-${index}`}
									name={template.name}
									contentTypeLabel={template.contentTypeLabel}
									contentTypeIcon={template.contentTypeIcon}
									imageSource={template.imageSource}
									imageSourceDark={template.imageSourceDark}
									createLink={template.createLink}
									templateId={template.templateId}
									contentType={template.contentType}
									onClose={onClose}
								/>
							);
						})}
					</div>
				</ModalBody>
			</Box>
			<Box xcss={stylesMap.footerStyles}>
				<div>
					<LinkButton
						href={blankDocCreateLink}
						onClick={handleBlankDocClick}
						testId={`${testId}-start-blank-doc`}
					>
						<Box xcss={stylesMap.iconButtonStyles}>
							<AddIcon label={formatMessage(messages.startBlankDocLabel)} />
							<Box>{formatMessage(messages.startBlankDoc)}</Box>
						</Box>
					</LinkButton>
				</div>

				<div>
					<LinkButton
						appearance="subtle"
						href={viewMoreTemplatesHref}
						onClick={handleViewMoreTemplatesClick}
						testId={`${testId}-view-more`}
					>
						<Box xcss={stylesMap.iconButtonStyles}>
							<Box>{formatMessage(messages.viewMoreTemplates)} </Box>
							<ArrowRightIcon label={formatMessage(messages.viewMoreTemplatesLabel)} />
						</Box>
					</LinkButton>
				</div>
			</Box>
		</Modal>
	);
};
